import React from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { RowSelectionState, PopupButton } from 'design-system-web';
import { bulkUpdateTaskStatusMutation } from 'graphql-queries';
import { getTaskStatusLabel } from 'domains-work-order-type-contracts';
import { TaskStatusEnum } from '../types/taskStatus';

const TASK_SELECTION_LIMIT = 100;

type Props = {
    rowSelection?: RowSelectionState;
    refetch?: () => void;
};

export function BulkStatusUpdateAction({ rowSelection, refetch }: Props) {
    const { t } = useTranslation();
    const [bulkUpdateTaskStatus] = useMutation(bulkUpdateTaskStatusMutation);

    const handleStatusUpdate = async (status: string) => {
        if (!status || !rowSelection) return;

        const selectedTaskIds = Object.entries(rowSelection)
            .filter(([_, isSelected]) => isSelected)
            .map(([taskId]) => taskId);

        if (selectedTaskIds.length === 0) {
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.noSelection')
            );

            return;
        }

        if (selectedTaskIds.length > TASK_SELECTION_LIMIT) {
            await window.Toast.show(
                t(
                    'web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.limitExceeded',
                    {
                        taskSelectionLimit: TASK_SELECTION_LIMIT,
                        selectedTaskCount: selectedTaskIds.length,
                    }
                ),
                5000
            );

            return;
        }

        try {
            await window.Alert.confirm({
                title: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.confirm.title'),
                message: t(
                    'web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.confirm.message',
                    {
                        count: selectedTaskIds.length,
                        status: getTaskStatusLabel(status),
                    }
                ),
                confirmText: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.confirm.button'),
                cancelText: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.cancel.button'),
            });
        } catch {
            return;
        }

        window.Alert.loading({
            title: t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.inProgress'),
            disableCloseOnBackgroundClick: true,
        });

        try {
            const result = await bulkUpdateTaskStatus({
                variables: {
                    taskIds: selectedTaskIds,
                    status,
                },
            });

            const { successCount, failureCount } =
                result.data?.bulkUpdateTaskStatus || {
                    successCount: 0,
                    failureCount: selectedTaskIds.length,
                };

            window.Alert.hide();

            if (failureCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.success', {
                        count: successCount,
                    })
                );
            } else if (successCount === 0) {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.error')
                );
            } else {
                await window.Toast.show(
                    t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.partial', {
                        successCount,
                        failureCount,
                    }),
                    5000
                );
            }

            if (refetch) {
                refetch();
            }
        } catch {
            window.Alert.hide();
            await window.Toast.show(
                t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.error')
            );
        }
    };

    const statusMenuItems = [
        TaskStatusEnum.Created,
        TaskStatusEnum.InProgress,
        TaskStatusEnum.OnHold,
        TaskStatusEnum.Completed,
        TaskStatusEnum.Withdrawn,
    ].map((status) => ({
        label: getTaskStatusLabel(status) || status,
        onSelect: () => handleStatusUpdate(status),
    }));

    return (
        <PopupButton
            variant="secondary"
            size="large"
            items={statusMenuItems}
            testId="bulk-task-status-update-button"
            style={{ marginRight: '0.5rem' }}
        >
            {t('web.admin.workOrder.preventiveMaintenance.task.bulkActions.updateStatus.selectStatus')}
        </PopupButton>
    );
}
